import type ru from './ru';

type Messages = { [K in keyof typeof ru]: { [P in keyof (typeof ru)[K]]: (typeof ru)[K][P] extends string ? string : Record<string, string> } };

const en: Messages = {
  app: {
    title: 'Visual Platform',
    myCanvases: 'My canvases',
    modules: 'Modules',
    settings: 'Settings',
    profile: 'Profile',
    beta: 'Beta',
    logs: 'Log',
    menu: 'Menu',
    back: 'Back',
    updateAvailable: 'A new platform version is available — reload the page when convenient. Your data is saved.',
  },
  home: {
    cover: 'Cover',
    coverPreview: 'Canvas preview',
    newCanvas: 'New canvas',
    add: 'Add',
    addNotebook: 'Notebook',
    addSpace: 'Space',
    inDevelopment: 'In development',
    import: 'Import',
    exportAll: 'Export all canvases',
    empty: 'No canvases yet. Create your first one.',
    untitled: 'Untitled',
    objects: '{n} object|{n} objects',
    updated: 'Updated {date}',
    open: 'Open',
    rename: 'Rename',
    duplicate: 'Duplicate',
    export: 'Export',
    delete: 'Delete',
    confirmDelete: 'Delete canvas “{name}”? This cannot be undone.',
    lastExport: 'Last export: {days} days ago',
    lastExportNever: 'No backup has been made yet',
    backupHint: 'Data is stored only in this browser. Export regularly to keep a backup.',
    storage: 'Storage: {used} of {quota}',
    storageWarn: 'Storage is almost full: {pct}%',
    persistDenied: 'The browser did not grant persistent storage — data may be evicted when space runs low.',
    search: 'Search canvases',
    pin: 'Pin',
    unpin: 'Unpin',
    pinned: 'Pinned',
    pick: 'Select',
    pickAll: 'All',
    pickNone: 'Clear',
    pickDone: 'Done',
    picked: '{n} selected|{n} selected',
    bulk: 'Actions for the selected canvases',
    confirmDeleteMany: 'Delete {n} canvas? This cannot be undone.|Delete {n} canvases? This cannot be undone.',
    layout: 'Canvas layout',
    layoutGrid: 'Tiles',
    layoutList: 'List',
    today: 'today, {time}',
    backupAction: 'make a backup',
    persistShort: 'Storage is not persistent',
  },
  canvas: {
    importAs: 'Import: {name}',
    exportAs: 'Export: {name}',
    saved: 'Saved',
    saving: 'Saving…',
    saveError: 'Save failed',
    loading: 'Loading… {n}',
    readOnly: 'Read-only: this canvas is open in another tab',
    takeOver: 'Take over',
    rename: 'Rename',
    duplicate: 'Duplicate',
    export: 'Export archive',
    exportPng: 'Export PNG',
    exportSvg: 'Export SVG',
    exportPdf: 'Export PDF',
    importSvg: 'Import SVG…',
    close: 'Close',
    namePrompt: 'Canvas name',
    quarantined: 'Corrupted records moved to quarantine: {n}',
    recovered: 'Recovered unsaved changes: {n}',
  },
  tools: {
    templates: 'Templates',
    title: 'Tools',
    pen: 'Pencil (B)',
    path: 'Pen (P)',
    pathHint: 'Click for a corner, drag for a curve (Alt: cusp). Click the first point to close, an end of a finished path to continue it. Enter or double-click finishes, Backspace removes a point',
    pathEditHint: 'Drag points and handles (Alt breaks handles). Click the outline to add a point, Alt-click a point for corner or smooth, Delete removes a point, Esc when done',
    draw: 'Drawing',
    eraser: 'Eraser (E)',
    more: 'More',
    penColor: 'Line color',
    penWidth: 'Width',
    penHue: 'Hue',
    penSat: 'Saturation',
    penLight: 'Lightness',
    penSharp: 'Sharp stroke',
    penSmooth: 'Smooth',
    penCustom: 'Custom color',
    brushTitle: 'Material',
    strokeTitle: 'Stroke',
    brush: {
      ink: 'Pen',
      highlighter: 'Highlighter',
    },
    select: 'Select (V)',
    hand: 'Hand (H)',
    lasso: 'Lasso (Q)',
    shapes: 'Shapes',
    rect: 'Rectangle (R)',
    pill: 'Capsule',
    node: 'Node',
    manualInput: 'Manual input',
    data: 'Data',
    document: 'Document',
    display: 'Display',
    bracket: 'Bracket with a comment',
    brace: 'Brace, note on the right',
    braceLeft: 'Brace, note on the left',
    ellipse: 'Ellipse (O)',
    triangle: 'Triangle',
    diamond: 'Diamond',
    text: 'Text (T)',
    sticker: 'Sticky note (S)',
    frame: 'Frame (F)',
    image: 'Image',
    lines: 'Lines',
    line: 'Line (L)',
    arrow: 'Arrow',
    connector: 'Connector (C)',
    moduleTools: 'Modules',
  },
  cmd: {
    unframe: 'Remove frame, keep contents',
    undo: 'Undo (Ctrl+Z)',
    redo: 'Redo (Ctrl+Shift+Z)',
    delete: 'Delete',
    duplicate: 'Duplicate (Ctrl+D)',
    copy: 'Copy (Ctrl+C)',
    cut: 'Cut (Ctrl+X)',
    paste: 'Paste (Ctrl+V)',
    copyPng: 'Copy as PNG',
    group: 'Group (Ctrl+G)',
    ungroup: 'Ungroup (Ctrl+Shift+G)',
    lock: 'Lock (Ctrl+L)',
    unlock: 'Unlock (Ctrl+L)',
    order: 'Order',
    front: 'Bring to front',
    forward: 'Bring forward',
    backward: 'Send backward',
    back: 'Send to back',
    align: 'Align',
    alignLeft: 'Align left',
    alignCenterX: 'Align center (horizontal)',
    alignRight: 'Align right',
    alignTop: 'Align top',
    alignCenterY: 'Align middle (vertical)',
    alignBottom: 'Align bottom',
    distributeX: 'Distribute horizontally',
    distributeY: 'Distribute vertically',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zoomReset: 'Zoom to 100%',
    fitAll: 'Fit all (Shift+1)',
    selectAll: 'Select all',
    editText: 'Edit text',
    showData: 'Raw data',
    exportSelection: 'Export selection',
    exportFrame: 'Export frame',
  },
  props: {
    style: 'Style',
    dockSide: 'Properties to the sidebar',
    dockTop: 'Properties back to the top bar',
    font: 'Font',
    title: 'Properties',
    frameUntitled: 'Frame',
    clipContent: 'Clip content',
    nothing: 'Nothing selected',
    multiple: '{n} objects selected',
    canvas: 'Canvas',
    stroke: 'Stroke',
    strokeColor: 'Stroke color',
    strokeWidth: 'Width',
    dash: 'Line style',
    solid: 'Solid',
    dashed: 'Dashed',
    fine: 'Fine dash',
    dotted: 'Dotted',
    none: 'None',
    fill: 'Fill',
    fillKind: 'Fill type',
    fillSolid: 'Solid',
    fillLines: 'Lines',
    fillGrid: 'Grid',
    fillNone: 'None',
    fillColor: 'Fill color',
    patternBg: 'Pattern background',
    fillType: 'Type',
    color: 'Color',
    patternColor: 'Line color',
    patternGap: 'Between lines',
    transparent: 'Transparent',
    density: 'Spacing',
    densitySparse: 'Sparse',
    densityMedium: 'Medium',
    densityDense: 'Dense',
    mixed: 'Mixed values',
    contour: 'Outline',
    line: 'Line',
    lineStyle: 'Style',
    noContour: 'No outline',
    arrowNone: 'No arrows',
    arrowBoth: 'Both ends',
    size: 'Size',
    hAlign: 'Horizontal',
    vAlign: 'Vertical',
    top: 'Top',
    middle: 'Middle',
    bottom: 'Bottom',
    fontStyle: 'Style',
    object: 'Object',
    editPoints: 'Points',
    editPointsHint: 'Edit the path points (double-click or Enter)',
    arrange: 'Order and groups',
    applyHint: 'Applies to {n} of {total}',
    opacity: 'Opacity',
    text: 'Text',
    fontSize: 'Font size',
    textColor: 'Text color',
    align: 'Alignment',
    bold: 'Bold',
    italic: 'Italic',
    position: 'Position and size',
    rotation: 'Rotation',
    cornerRadius: 'Corner radius',
    arrows: 'Arrows',
    arrowStart: 'Start',
    arrowEnd: 'End',
    route: 'Routing',
    routeOrthogonal: 'Orthogonal',
    routeStraight: 'Straight',
    routeCurved: 'Curved',
    labelAdd: 'Label',
    labelEdit: 'Edit label',
    labelHint: 'Text on the arrow (or double-click it)',
    locked: 'Locked',
    type: 'Type',
    module: 'Module',
    name: 'Name',
    moduleProps: 'Module properties',
    stale: 'Data is stale — the module did not respond in time',
    links: 'Links',
    addLink: 'Link to…',
    linkHint: 'Select two objects to create a data link',
    createLink: 'Create link',
    removeLink: 'Remove link',
    unavailable: 'This object could not be displayed. The functionality to display it is unavailable.',
  },
  grid: {
    title: 'Grid',
    show: 'Show grid',
    kind: 'Style',
    dots: 'Dots',
    lines: 'Lines',
    cells: 'Cells',
    step: 'Step',
    color: 'Color',
    snap: 'Snap to grid',
  },
  tree: {
    title: 'Objects',
    empty: 'Canvas is empty',
    group: 'Group',
    search: 'Find an object',
    nothingFound: 'Nothing found',
  },
  types: {
    'core:rect': 'Rectangle',
    'core:ellipse': 'Ellipse',
    'core:triangle': 'Triangle',
    'core:diamond': 'Diamond',
    'core:text': 'Text',
    'core:sticker': 'Sticky note',
    'core:frame': 'Frame',
    'core:image': 'Image',
    'core:line': 'Line',
    'core:connector': 'Connector',
    'core:group': 'Group',
    'core:path': 'Path',
    'core:diagram': 'Diagram',
    'core:pen': 'Drawing',
  },
  search: {
    filters: 'Filters',
    byType: 'Type',
    byColor: 'Color',
    resetFilters: 'Reset filters',
    title: 'Find on canvas',
    placeholder: 'Find on canvas',
    none: 'not found',
    prev: 'Previous (Shift+Enter)',
    next: 'Next (Enter)',
  },
  keys: {
    palette: 'Command palette',
    style: 'Copy / paste style',
    notes: 'Comments on the object',
    modules: 'Modules',
    saved: 'Saved',
    save: 'Save now',
    next: 'Next / previous object',
    shapeOps: 'Union / subtract / intersect / exclude shapes',
    title: 'Keyboard shortcuts',
    tools: 'Tools',
    edit: 'Editing',
    view: 'View and navigation',
    mouse: 'Mouse',
    clipboard: 'Copy / cut / paste',
    group: 'Group / ungroup',
    lock: 'Lock / unlock',
    order: 'Forward / backward',
    orderEdge: 'To front / to back',
    nudge: 'Nudge by 1 (10) units',
    escape: 'Cancel / clear selection',
    zoom: 'Zoom in / out',
    fitSelection: 'Zoom to selection',
    viewBack: 'Previous view (Alt+←)',
    viewForward: 'Next view (Alt+→)',
    zen: 'Zen: hide the panels. Esc brings them back',
    addSelect: 'Add to selection',
    keepRatio: 'Keep proportions / axis while moving',
    noSnap: 'No guides while moving',
    ctrlDrag: 'Ctrl (⌘) + drag',
    dragCopy: 'Duplicate and move the copy',
    dblclick: 'Edit text / action on empty space',
    shiftClick: 'Shift + click',
    doubleClick: 'Double click',
  },
  zen: {
    enter: 'Zen',
    exit: 'Leave Zen',
    on: 'Zen. Panels are hidden',
  },
  minimap: {
    title: 'Minimap',
    hint: 'Click or drag to navigate; wheel to zoom',
  },
  perf: {
    title: 'Performance',
    fps: 'FPS',
    frame: 'Frame, ms',
    backend: 'Graphics',
    objects: 'Objects',
    visible: 'Visible',
    drawCalls: 'Draw calls',
    textures: 'Textures',
    memory: 'JS memory',
    workers: 'Modules (Workers)',
    api: 'Module API, ms',
    index: 'Index',
    autosave: 'Autosave, ms',
    load: 'Canvas load, ms',
    unavailable: 'unavailable',
    generate: 'Generate',
    switchBackend: 'Switch',
  },
  settings: {
    onAccent: 'Text and icons on buttons',
    onAccentHint: 'On accent-colored buttons',
    accentInk: 'Accent text and icons',
    accentInkHint: 'Selected items, active buttons and icons',
    inkAuto: 'Auto',
    inkAutoNow: 'chosen to stay readable',
    inkLowContrast: 'The chosen color is hard to read (contrast {ratio}:1, 4.5:1 or more needed).',
    secAppearance: 'Appearance',
    secStorage: 'Storage',
    secAbout: 'About',
    theme: 'Theme',
    themeHint: 'Light, dark or as the system',
    homeLayout: 'My canvases',
    homeLayoutHint: 'Tiles or a list on the home screen',
    glass: 'Transparency',
    glassHint: 'The menu and the canvas panel. Higher lets more of what is behind show through',
    noMotion: 'Turn animations off',
    noMotionHint: 'Panel transparency, the name-card tilt and the other effects',
    theme_system: 'System',
    theme_light: 'Light',
    theme_dark: 'Dark',
    accent: 'Accent color',
    accentHint: 'Color of buttons, selection and active controls',
    accentPreview: 'Buttons look like this',
    accentSample: 'Selected',
    darkCanvas: 'Dark canvas in the dark theme',
    darkCanvasHint: 'White turns dark and black text light, hues are kept. Pictures and exports keep their colors',
    title: 'Settings',
    language: 'Language',
    renderer: 'Rendering',
    renderer2d: 'Canvas 2D',
    marquee: 'Selection box picks',
    marqueeIntersect: 'Touched by the box',
    marqueeContain: 'Fully inside',
    emptyDrag: 'Dragging on empty space',
    emptyDragMarquee: 'Selects',
    emptyDragPan: 'Pans the canvas',
    emptyDbl: 'Double click on empty space',
    emptyDblText: 'Creates text',
    emptyDblZoom: 'Zooms in',
    emptyDblNone: 'Does nothing',
    rightDrag: 'Pan the canvas with the right button',
    rightDragHint: 'Hold the right mouse button and drag to move the canvas. A short click still opens the menu.',
    devMode: 'Developer mode',
    devModeHint: 'Allows installing third-party modules from files.',
    devModeWarn:
      'Third-party modules run foreign code. Isolation in this version protects against bugs, not against malicious code. Install modules only from sources you trust.',
    verbose: 'Verbose debugging',
    verboseHint: 'Writes full object data to the log. Turns off automatically after 15 minutes or when the editor closes.',
    perfPanel: 'Performance panel',
    secGeneral: 'General',
    secCanvas: 'Canvas',
    secControl: 'Controls',
    secPerformance: 'Performance',
    secDeveloper: 'For developers',
    languageHint: 'Language of the interface and module labels.',
    backgroundHint: 'Color under all objects.',
    grid: 'Grid',
    gridStep: 'Grid step',
    snap: 'Snap to grid',
    snapHint: 'Objects land on grid nodes when moved or resized.',
    smartGuides: 'Smart guides',
    smartGuidesHint: 'Suggest alignment to edges and centers of nearby objects.',
    minimap: 'Minimap',
    minimapHint: 'Canvas map in the bottom-right corner for quick navigation.',
    emptyDragHint: 'What dragging on an empty spot does.',
    emptyDblHint: 'Empty space and the inside of a frame: create text, zoom in twofold, or do nothing.',
    marqueeHint: 'Which objects the selection box picks.',
    rendererHint: 'WebGL 2 is faster on large canvases; Canvas 2D is for compatibility.',
    webgl: 'WebGL 2',
    textLodHint: 'Text smaller than this on screen is drawn as a bar — faster when zoomed out.',
    perfPanelHint: 'FPS, object count, memory and operation timings.',
    verboseLeft: '{n} min left',
    off: 'off',
    backgroundColor: 'Canvas background',
    textLod: 'Simplify small text',
  },
  sidebar: {
    panelSec: 'Module panel',
    showButton: 'Button in the right bar',
    showButtonHint: 'The module button next to search opens its panel on the right',
    hideButton: 'Remove the button from the right bar',
    rightClickHide: 'right click removes the button',
    buttonHidden: 'Button removed. Bring it back: Settings → Modules → module page',
    buttonShown: 'The button is back in the right bar',
    panelsMenu: 'Module panels',
    panelsLimit: 'Up to {n} buttons in the right bar, in this order. Open the others from here.',
    overLimit: 'no room',
    moveUp: 'Up',
    moveDown: 'Down',
  },
  chat: {
    open: 'Chat',
    close: 'Close chat',
    channels: 'Chat section',
    ai: 'AI',
    team: 'Team',
    personal: 'Personal',
    placeholder: 'Message…',
    send: 'Send',
    emptyAi: 'Ask about this canvas. The conversation stays with it.',
    emptyTeam: 'Team messages for this canvas.',
    emptyPersonal: 'Notes for this canvas — on this device only.',
    you: 'You',
    reply: 'Reply',
    deleteMsg: 'Delete',
    replying: 'Reply',
    replyGone: 'Message deleted',
  },
  feed: {
    open: 'Feed',
    close: 'Close feed',
    sections: 'Feed section',
    chat: 'Chat',
    notices: 'Notifications',
    templates: 'Templates',
    tools: 'Tools',
    toolsPlatform: 'Platform',
    toolsModules: 'Modules',
    toolsOpenCanvas: 'Module tools work with a canvas — open one.',
    tool: {
      palette: 'Command palette',
      paletteHint: 'Any action by its name (Ctrl+K)',
      findHint: 'Text on the board (Ctrl+F)',
      diagramHint: 'A diagram from text',
    },
    noticesEmpty: 'No notifications yet.',
    templatesEmpty: 'No templates yet.',
    syncHint: 'Once the cloud is connected, this section can update from other devices.',
    emptyAi: 'Write without a canvas, or type ! and a name to pick one from the list.',
    emptyPersonal: 'Personal notes stay on this device. ! ties a note to a canvas.',
    linked: 'Canvas',
    canvasGone: 'Canvas deleted',
    pickCanvas: 'Canvases',
  },
  profiles: {
    placeTitle: 'Where to keep canvases',
    warnTitle: 'Warning',
    warnBrowser: 'Clearing the browser data also deletes the profile data.',
    warnFolder: 'The data is stored on the device in the open. It can be stolen, changed or deleted. That is how the app works.',
    placeFixed: 'The place is chosen once and does not change later.',
    placeNoFolder: 'Keeping canvases in a folder works in Chrome, Edge, Opera and Yandex Browser on computers. The place does not change later.',
    folderPick: 'Choose a folder…',
    folderOther: 'Another folder…',
    folderIsShared: 'This is a shared folder of profiles: this profile gets its own subfolder in it.',
    folderFailed: 'The folder could not be chosen: {msg}',
    title: 'Profiles',
    firstTitle: 'Create a profile',
    firstLead: 'Your canvases, modules and settings live in a profile. It stays on this device.',
    newTitle: 'New profile',
    chooseTitle: 'Who\'s here?',
    defaultName: 'My profile',
    create: 'Create profile',
    createFailed: 'The profile was not created: {msg}',
    new: 'New profile',
    import: 'Import a profile from a file',
    importFailed: 'The profile was not imported: {msg}',
    imported: 'Profile "{name}" was added to this device',
    export: 'Export profile',
    exportFailed: 'The profile was not exported: {msg}',
    exportFirst: 'Export the profile first',
    switch: 'Switch profile',
    actions: 'Profile actions',
    other: 'Choose another profile',
    onDevice: 'Profiles on this device',
    onDeviceHint: 'Every profile has its own canvases, modules, settings and chats',
    limit: 'A device can have at most {n} profiles',
    locked: 'Protected with a PIN',
    lockedTitle: 'The profile is locked',
    lockNow: 'Lock',
    unlock: 'Enter',
    protect: 'Protect with a PIN',
    pin: 'PIN',
    pinPlaceholder: 'PIN',
    pinNew: 'New PIN (4–8 digits)',
    pinRepeat: 'Repeat the PIN',
    pinCurrent: 'Current PIN',
    pinCurrentWrong: 'The current PIN is wrong',
    pinMismatch: 'The PINs do not match',
    pinNote: 'A PIN keeps the profile from a casual look, but it does not encrypt the data: it lies open in the browser or in the folder. A forgotten PIN cannot be reset — only the profile can be deleted.',
    pinWrong: 'Wrong PIN. Tries left before a pause: {n}',
    pinWait: 'Too many wrong tries. The next one in {time}',
    pinOn: 'A PIN is set',
    pinOff: 'No PIN',
    pinSaved: 'The PIN is saved',
    pinRemoved: 'The PIN is removed',
    autoLock: 'Lock after idle time',
    autoLockOff: 'Never',
    minutes: '{n} min',
    forgot: 'Forgot the PIN? Delete the profile',
    forgotPin: 'Forgot the PIN',
    delete: 'Delete profile',
    deleteTitle: 'Delete the profile "{name}"?',
    deleteWhat: '{n} canvas, the modules, settings and chats of this profile will be deleted. This cannot be undone.|{n} canvases, the modules, settings and chats of this profile will be deleted. This cannot be undone.',
    deleteWhatUnknown: 'All canvases, modules, settings and chats of this profile will be deleted. This cannot be undone.',
    deleteFolder: 'The canvas files in the folder "{folder}" will be deleted too.',
    deleteType: 'To confirm, type the profile name: {name}',
    deleteForever: 'Delete forever',
    deleted: 'The profile is deleted',
    deletedFolderLeft: 'The profile is deleted, but its canvas folder could not be reached — the files in it stay.',
    pictureRemove: 'Remove',
    pictureTooBig: 'The picture is bigger than 5 MB',
    pictureBad: 'This is not a picture (PNG, JPEG, WebP, GIF fit)',
    folderKindTitle: 'How to use the folder?',
    folderKind: 'The folder is empty. It can hold this profile’s canvases only, or be shared by several profiles of this device — each in its own subfolder.',
    folderOwn: 'This profile only',
    folderShared: 'Shared by profiles',
    folderOwned: 'This folder belongs to another profile. Choose another folder or a shared folder of profiles.',
  },
  look: {
    title: 'Look: {name}',
    tray: 'tray',
    trayButton: 'Tray look',
    looks: 'Look',
    edit: 'Card look',
    reset: 'Reset',
    done: 'Done',
    background: 'Background',
    bg: { none: 'None', color: 'Colour', gradient: 'Gradient', image: 'Picture' },
    color: 'Colour',
    colors: 'Colours',
    gradient: { linear: 'Linear', radial: 'Radial' },
    angle: 'Angle',
    fit: 'Placement',
    'fit.cover': 'Fill',
    'fit.contain': 'Fit',
    'fit.tile': 'Tile',
    otherImage: 'Another picture',
    motion: 'Motion',
    motions: { none: 'None', gradient: 'Gradient', particles: 'Particles', waves: 'Waves', shine: 'Shine' },
    opacity: 'Opacity',
    blur: 'Blur',
    radius: 'Radius',
    border: 'Border',
    borderWidth: 'Border width',
    kind: 'Type',
    frame: 'Frame and shadow',
    shadow: 'Shadow',
    shadows: { none: 'None', soft: 'Soft', strong: 'Strong' },
  },
  cardEditor: {
    title: 'Card editor',
    back: 'Profile',
    titlePlaceholder: 'Card name',
    untitled: 'Untitled',
    undo: 'Undo',
    redo: 'Redo',
    grid: '8 px grid',
    preview: 'Preview',
    add: 'Add to profile',
    save: 'Save',
    added: 'The card is on your profile',
    updated: 'Card saved',
    remove: 'Delete card',
    removeAsk: 'Delete the card? Its state goes too.',
    leaveAsk: 'There are unsaved changes. Leave without saving?',
    leave: 'Leave',
    limit: 'At most 20 cards — delete one you no longer need',
    tooBig: 'The picture is over 500 KB',
    pasteHint: 'Copy a picture and press Ctrl+V — up to 500 KB',
    elements: 'Elements',
    templates: 'Templates',
    mine: 'My cards',
    new: 'New card',
    card: 'Card',
    deleteEl: 'Delete element',
    keysHint: 'Arrows move (Shift — by 8), Delete removes, Ctrl+V pastes a picture',
    previewHint: 'The card works as on the profile; preview state is not kept',
    el: { rect: 'Rectangle', circle: 'Circle', line: 'Line', text: 'Text', icon: 'Icon', image: 'Picture', button: 'Button', toggle: 'Switch', checkbox: 'Checkbox', counter: 'Counter', note: 'Note', time: 'Time', date: 'Date', weekday: 'Weekday', countdown: 'Countdown', profile: 'Profile counter' },
    p: { text: 'Text', colors: 'Colours', color: 'Colour', fill: 'Fill', size: 'Size', bold: 'Bold', align: 'Alignment', link: 'Link', range: 'Min · max · step', min: 'Minimum', max: 'Maximum', step: 'Step', date: 'Date', stat: 'Figure', radius: 'Radius', opacity: 'Opacity', rotate: 'Rotation', effect: 'Effect', trigger: 'When', cardSize: 'Size' },
    align: { left: 'Left', center: 'Centre', right: 'Right' },
    stat: { canvases: 'Canvases', objects: 'Objects', opens: 'Opens', days: 'Days' },
    fx: { none: 'None', appear: 'Appear', pulse: 'Pulse', sway: 'Sway', spin: 'Spin', shimmer: 'Shimmer' },
    tr: { always: 'Always', hover: 'Hover', press: 'Press', change: 'Change' },
    tpl: { habits: 'Habits', habit1: 'Exercise', habit2: 'A walk', habit3: 'Read 20 minutes', water: 'Water', waterText: 'Glasses of water today', countdown: 'Countdown', countdownText: 'Until New Year', note: 'Note', notePlaceholder: 'Write something…' },
  },
  profile: {
    title: 'Profile',
    canvases: 'Canvases',
    objects: 'Objects',
    popular: 'Most popular canvas',
    clock: 'Clock',
    popularEmpty: 'No canvases yet',
    firstName: 'First name',
    lastName: 'Last name',
    avatar: 'Avatar photo',
    avatarChoose: 'Choose photo',
    editStats: 'Edit',
    library: 'Panel library',
    panels: 'Panels',
    editCard: 'Edit card',
    libraryEmpty: 'Every panel is already on the profile',
    addPanel: 'Add',
    stickers: 'Stickers',
    stickersHint: 'Click one to stick it on the card',
    stickersFull: 'No room for more stickers',
    removeSticker: 'Peel off the sticker',
    cardMakers: 'Make a card',
    openMaker: 'Open',
    cardLinkAsk: 'A module card opens {url}. Open it?',
    cardLinkOpen: 'Open',
    removePanel: 'Remove panel',
    deleteData: 'Delete data',
    exportData: 'Export',
    security: 'Security',
    pinSet: 'Set a PIN',
    pinChange: 'Change PIN',
    pinDelete: 'Remove PIN',
    passkey: 'Add a passkey',
    unset: 'Not set',
  },
  chatSettings: {
    title: 'Chat settings',
    hint: 'Chat messages are stored in this browser',
    open: 'Manage',
    empty: 'No saved messages.',
    feed: 'Feed (no canvas)',
    count: '{n} message|{n} messages',
    deleteFor: 'Delete messages: {name}',
    deleteAll: 'Delete all messages',
    confirmFor: 'Delete all chat messages of “{name}”? This cannot be undone.',
    confirmAll: 'Delete all saved messages in every chat? This cannot be undone.',
  },
  modules: {
    dependency: 'The module needs another module: {what}. Install it first.',
    dependentsBlock: 'Other modules need this one: {what}. Remove them first.',
    requires: 'Requires',
    templateFailed: 'The template was not inserted: {msg}',
    importFailed: 'Import failed: {msg}',
    exportFailed: 'Export failed: {msg}',
    title: 'Modules',
    confirmAccess: 'Confirm access',
    installUpdate: 'Update from file',
    devOnly: 'Installing third-party modules is available in developer mode only.',
    enableDev: 'Enable developer mode',
    none: 'No modules installed',
    enabled: 'Enabled',
    disabled: 'Disabled',
    isolated: 'Isolated',
    crashed: 'Crashed',
    running: 'Running',
    incompatible: 'Incompatible',
    version: 'Version {v}',
    author: 'Author: {a}',
    api: 'API: {r}',
    permissions: 'Permissions',
    perm: {
      'canvas.read': 'Read the canvas',
      'canvas.write': 'Change the canvas',
      files: 'Files',
      clipboard: 'Clipboard',
      mcp: 'Call other modules (AI)',
      feed: 'Tools in the feed',
      'canvas.create': 'Create canvases',
      keyboard: 'Keyboard',
      fullscreen: 'Full screen',
      'pointer-lock': 'Pointer capture',
      'profile.cards': 'Profile cards',
      'profile.stats': 'Profile counters',
    },
    cardUses: 'Profile cards use',
    use: {
      'action': 'action buttons',
      'link': 'links to canvases and addresses',
      'toggle': 'switches and checkboxes',
      'counter': 'counters',
      'note': 'notes',
      'bind': 'values from the module',
      'live:time': 'time and date',
      'live:timer': 'stopwatch',
      'live:countdown': 'countdown',
      'live:profile': 'profile counters',
      'anim:appear': '"appear" effect',
      'anim:pulse': '"pulse" effect',
      'anim:sway': '"sway" effect',
      'anim:spin': '"spin" effect',
      'anim:shimmer': '"shimmer" effect',
      'anim:custom': 'own animations',
      'font': 'own fonts',
      'image': 'own pictures',
      'look:image': 'background picture',
      'look:motion': 'animated background',
    },
    keyboardHere: 'The module has the keyboard · Esc to take it back',
    sandboxUnsafe: 'Module content is not shown: its sandbox is not isolated. Tell the server administrator.',
    enable: 'Enable',
    disable: 'Disable',
    uninstall: 'Uninstall',
    rollback: 'Roll back to {v}',
    restart: 'Restart',
    confirmUninstall: 'Uninstall module “{name}”? Its objects are kept and shown as unavailable.',
    installed: 'Module “{name}” {v} installed',
    updated: 'Module “{name}” updated to {v}',
    invalid: 'Module failed validation: {reason}',
    incompatibleApi: 'Module requires API {range}, platform provides {api}',
    oldApi: 'The module was made for an older platform version (API {range}). A version of the module for API {api} is needed.',
    device: {
      open: 'Module “{name}” wants to open a file from your computer.',
      save: 'Module “{name}” wants to save a file to your computer.',
      read: 'Module “{name}” wants to read the clipboard.',
      write: 'Module “{name}” wants to write to the clipboard.',
      allow: 'Allow',
      denied: 'The user did not allow it',
      tooLarge: 'File “{name}” is too large',
    },
    isolatedMsg: 'Module “{name}” was isolated due to violations: {reason}',
    crashedMsg: 'Module “{name}” crashed and will be restarted',
    updateIncompatible:
      'The new version of “{name}” is incompatible with {n} existing objects. Roll back the update? (OK — roll back, Cancel — keep the new version; objects stay unavailable)',
    objects: 'Objects on open canvases: {n}',
    bundled: 'Built-in',
    trustNote: 'Developer mode: unsigned modules install from a file. Catalog modules are checked and marked "Official".',
    grant: 'Grant',
    conflict: 'Operation “{op}” is blocked: the module is processing this object',
    ops: { move: 'move', resize: 'resize', rotate: 'rotate', delete: 'delete', style: 'style change' },
    permissionsTitle: 'Module permissions',
    permissionsAsk: 'The module requests access. Choose what to allow:',
    accessAsk: 'Module “{name}” wants access to data:',
    commands: 'Commands',
    revoke: 'Revoke',
    hint: 'Platform extensions',
    search: 'Search by name or features',
    nothingFound: 'Nothing found',
    back: 'All modules',
    open: 'Open module',
    settingsOf: 'Settings of “{name}”',
    toggleOf: 'Enable “{name}”',
    about: 'About',
    versionLabel: 'Version',
    apiLabel: 'API version',
    publisher: 'Publisher',
    links: 'Publisher links',
    linkKind: { website: 'Website', repository: 'Repository', docs: 'Docs', support: 'Support', email: 'E-mail', github: 'GitHub', gitlab: 'GitLab', telegram: 'Telegram', vk: 'VK', x: 'X', youtube: 'YouTube', discord: 'Discord', other: 'Link' },
    buildDate: 'Build date',
    license: 'License',
    source: 'Source',
    idLabel: 'Identifier',
    objectsLabel: 'Objects on open canvases',
    features: 'Features',
    featObjects: 'Objects',
    featTools: 'Tools',
    featCommands: 'Commands',
    featLinks: 'Link types',
    featMcp: 'Tools for AI',
    status: { experimental: 'Experimental', beta: 'Beta', test: 'Test' },
    statusHint: {
      experimental: 'Experimental: may change or disappear without notice',
      beta: 'Beta: works, still being polished',
      test: 'Test module: for checking the platform and the API',
    },
    settingsSec: 'Module settings',
    noFeatures: 'The module adds nothing to the canvas',
    access: 'Access rights',
    accessHint: 'The module gets only what you allow. Rights can be revoked at any time.',
    noPerms: 'The module requests no access',
    scope: {
      all: 'All',
      own: 'Own',
      none: 'None',
    },
    permDesc: {
      'canvas.read.all': 'See every object and link of the open canvas',
      'canvas.read.own': 'See only its own objects: of its types and those it created, until you change them',
      'canvas.write.all': 'Create objects and change any object of the canvas',
      'canvas.write.own': 'Create objects and change only its own: of its types and those it created, until you change them',
      files: 'Open and save files',
      clipboard: 'Read and write the clipboard',
      mcp: 'Call tools of other modules — this is how an AI assistant works with their objects',
      feed: 'Show its tools in the Tools section of the feed',
      'canvas.create': 'Create new canvases (for example, when moving work from other programs) and open them on your action',
      keyboard: 'Receive key presses while its object is selected or its view or window is open (except Esc and Ctrl/Cmd combinations)',
      fullscreen: 'Show its view or window on the whole screen',
      'pointer-lock': 'Hide the mouse pointer and receive its movement (games, 3D); Esc to leave',
      'profile.cards': 'Make its own cards for the profile (without scripts)',
      'profile.stats': 'Show the number of canvases, objects, opens and days with the platform in its cards',
    },
    manage: 'Manage',
    previous: 'Previous versions',
    stopped: 'The module is not running',
  },
  diagram: {
    tool: 'Diagram (DSkhema)',
    title: 'Diagram',
    editTitle: 'Diagram code',
    code: 'Code',
    codeHint: 'Edit the diagram code (double-click)',
    preview: 'Preview',
    templates: 'Templates',
    tplFlow: 'Flowchart',
    tplSequence: 'Sequence',
    tplBlank: 'Blank flow',
    cheatsheet: 'Cheat sheet',
    notation: 'Notation',
    meaning: 'Meaning',
    apply: 'Done',
    applyHint: 'Ctrl+Enter',
    cancel: 'Cancel',
    noErrors: 'No errors',
    fix: 'Fix',
    fixTo: 'Change to “{text}”',
    fixAll: 'Fix all',
    errors: 'Errors: {n}',
    warnings: 'warnings: {n}',
    line: 'Line {n}',
    stale: 'Showing the last version without errors',
    naturalSize: 'Natural size',
    explode: 'Break into objects',
    explodeHint: 'Replace the diagram with ordinary shapes and connectors in a frame',
    confirmTemplate: 'Replace the current code with the template?',
    outdatedVersion: 'This diagram uses an old language version. Open Code and press Done.',
    explodeOutdated: 'Update the diagram first: open Code and press Done.',
  },
  flow: {
    direction: 'Direction',
    directionHint: 'Which way the diagram goes',
    directionNote: 'The header of the diagram code changes; the layout is rebuilt.',
    arrange: 'Arrange',
    arrangeHint: 'Place the selected shapes and the arrows between them as a flowchart',
    arrangeNote: 'Shapes keep their size, the first one stays put; arrows are routed again. Undo — Ctrl+Z.',
    down: 'Down',
    right: 'Right',
    up: 'Up',
    left: 'Left',
    addConnected: 'Add a connected shape',
    collapse: 'Collapse branches',
    expand: 'Expand branches',
  },
  logs: {
    title: 'Diagnostics log',
    export: 'Export log',
    clear: 'Clear',
    empty: 'No entries',
    retention: 'Entries are kept locally for 30 days and are never sent anywhere.',
    contentWarn: 'The log contains object data from verbose debugging. Export anyway?',
    level: 'Level',
    all: 'All',
    errors: 'Errors',
    warnings: 'Warnings',
    info: 'Info',
    debug: 'Debug',
    search: 'Search code and data',
    today: 'Today',
    yesterday: 'Yesterday',
    confirmClear: 'Delete all log entries?',
    refresh: 'Refresh',
    shown: 'Showing {n} of {total}',
  },
  export: {
    title: 'Export',
    scope: 'What to export',
    selection: 'Selected objects',
    view: 'Visible area',
    all: 'Whole canvas',
    frame: 'Frame',
    frameHint: 'The frame with its contents, cropped exactly to its edges',
    frames: 'Frames',
    framePages: 'Each frame a page',
    scale: 'Scale',
    background: 'Background',
    transparent: 'Transparent',
    color: 'Color',
    unavailable: 'Unavailable objects',
    placeholder: 'Placeholder',
    skip: 'Skip',
    tooLarge: 'Image is too large ({w}×{h}). Reduce the scale or area.',
    format: 'Format',
    go: 'Export',
    cancel: 'Cancel',
  },
  import: {
    svgEmpty: 'The SVG has no shapes that can be brought over',
    svgPartial: 'Objects added: {n}. Elements left out: {skipped} (clips, masks, filters, external pictures)',
    svgInvalid: '{name} is not an SVG or is damaged',
    tooNew: 'The archive was created by a newer platform version ({v}). Reload the page or update the platform.',
    invalid: 'The archive is corrupted or has an invalid format: {reason}',
    done: 'Canvases imported: {n}',
    missingModules: 'Modules not installed: {list}. Their objects are kept and shown as unavailable.',
  },
  clipboard: {
    denied: 'No access to the system clipboard — using the internal clipboard.',
    bigData: 'The data of these objects ({size}) will be copied too. Continue?',
    copied: 'Copied',
    pngFailed: 'Could not copy the image',
  },
  error: {
    readOnly: 'The canvas is read-only',
    coordLimit: 'Cannot place an object that far: coordinate limit ±1,000,000,000 reached',
    undoBlocked: 'This step cannot be undone while module “{module}” is unavailable',
    undoIncompatible: 'This step was recorded with an incompatible data format of module “{module}”',
    cycle: 'Link rejected: it would create a dependency cycle',
    unknown: 'Error: {msg}',
    webgl: 'WebGL 2 is unavailable — using Canvas 2D',
  },
  common: {
    ok: 'OK',
    cancel: 'Cancel',
    close: 'Close',
    save: 'Save',
    yes: 'Yes',
    no: 'No',
    copy: 'Copy',
    download: 'Download',
  },
  rich: {
    toolbar: 'Text formatting',
    done: 'Done',
    doneHint: 'Finish editing (Esc)',
    bold: 'Bold (Ctrl+B)',
    italic: 'Italic (Ctrl+I)',
    underline: 'Underline (Ctrl+U)',
    strike: 'Strikethrough',
    color: 'Text color',
    highlight: 'Highlight',
    noHighlight: 'No highlight',
    defaultColor: 'Same as the object',
    font: 'Font of the fragment',
    defaultFont: 'Same as the object',
    smaller: 'Smaller',
    larger: 'Larger',
    clear: 'Clear formatting',
  },
  fonts: {
    inter: 'Inter',
    interHint: 'Main font: crisp and neutral',
    serif: 'PT Serif',
    serifHint: 'Serif — for long texts',
    mono: 'JetBrains Mono',
    monoHint: 'Monospaced — for code',
    hand: 'Caveat',
    handHint: 'Handwritten — for notes',
    andika: 'Andika',
    andikaHint: 'Easy to read: clear letters for beginning readers and dyslexia',
    dyslexic: 'OpenDyslexic',
    dyslexicHint: 'For dyslexia: weighted letter bottoms. Cyrillic comes from Andika',
    system: 'System',
    systemHint: 'The operating system font (not embedded in SVG)',
  },
  changelog: {
    menu: 'What\'s new',
    title: 'What\'s new',
    history: 'History of changes',
    gotIt: 'Got it',
  },
  feedback: {
    menu: 'Report a problem',
    title: 'Report a problem',
    to: 'The letter goes to',
    kind: {
      bug: 'Bug',
      idea: 'Suggestion',
      question: 'Question',
    },
    subject: 'Subject',
    subjectPh: 'Subject of your message',
    placeholder: {
      bug: 'What happened, and what were you doing before?',
      idea: 'What is missing or could be better?',
      question: 'What would you like to ask?',
    },
    attach: 'Attach technical details',
    showInfo: 'What will be attached',
    hideInfo: 'Hide',
    note: 'Your mail program opens with the letter ready: you see all of it and send it yourself. The content of your canvases is not attached.',
    cut: 'The letter is long: your mail program may cut it. Press "Copy" and paste the whole text.',
    open: 'Open the letter',
    copy: 'Copy',
    opened: 'The letter is open in your mail program',
    copied: 'Copied: address, subject and text',
  },
  pwa: {
    install: 'Install the app',
    offline: 'No network. The app keeps working; everything is saved on this device',
    offlineBadge: 'offline',
    offlineHint: 'The module catalog and the documents open when the network is back; canvases work as usual',
    updateNow: 'Update',
  },
  storage: {
    place: {
      title: 'Where canvases are kept',
      browser: 'In the browser',
      folder: 'In a folder',
      change: 'Another folder…',
      browserHint: 'This profile keeps its canvases in the browser. The place was chosen when the profile was created and does not change.',
      folderHint: 'This profile keeps its canvases as files in the folder; settings, the journal and modules are in the browser. The place was chosen when the profile was created and does not change.',
      copyTitle: 'Copy the canvases?',
      copyToFolder: 'There is {n} canvas in the browser. Copy it into the folder? The browser keeps its copy.|There are {n} canvases in the browser. Copy them into the folder? The browser keeps its copies.',
      copyToBrowser: 'There is {n} canvas in the folder. Copy it into the browser? The folder keeps its files.|There are {n} canvases in the folder. Copy them into the browser? The folder keeps its files.',
      copyYes: 'Copy',
      copyNo: 'Do not copy',
      copying: 'Copying canvases: {done} of {total}…',
      failed: 'The folder could not be opened: {msg}',
    },
    gate: {
      title: 'Canvases are kept in a folder',
      missingTitle: 'The folder with the canvases cannot be reached',
      text: 'After a restart the browser asks to confirm access to the folder. Press "Grant access" and agree in the browser\'s window.',
      missing: 'The folder was renamed, moved or deleted. Choose it in its new place or switch the profile.',
      error: 'The folder could not be opened. Choose it again or switch the profile.',
      denied: 'The browser did not give access. Try again and choose "Allow".',
      grant: 'Grant access',
      pick: 'Choose a folder…',
      browser: 'Work in the browser',
      note: 'If the folder was renamed or moved, choose it in its new place: only the folder with this profile’s canvases fits.',
      notThisProfile: 'This folder has no canvases of this profile. Choose the folder the profile used.',
    },
    healthFolder: 'No access to the folder "{name}" with the canvases: changes are only in memory for now. Grant access again or put the folder back.',
    healthTitle: 'Data is not being saved',
    healthUnavailable: 'The browser does not let this site save data (maybe a private window or storage is blocked). Everything you draw will be gone when the tab closes. You can save canvases to a file with "Export".',
    healthQuota: 'The browser storage is full: the latest changes are NOT saved. Free up space (Settings → Storage) or export the canvas while the tab is open.',
    healthBlocked: 'Data cannot be written: another tab or the browser is blocking the database. Changes are only in memory for now — export the canvas while the tab is open.',
    healthFailed: 'A write error: the latest changes may not be saved. Export the canvas while the tab is open.',
    healthLimited: 'The browser storage is limited ({quota}) — this looks like a private window: data is deleted when it closes. Save canvases to a file.',
    exportNow: 'Export canvas',
    openStorage: 'Storage',
    leaveWarn: 'There are unsaved changes.',
    recovered: 'Saving works again',
    attachmentFailed: 'Could not save the file: the storage is full',
    canvases: 'Canvases',
    attachments: 'Attachments',
    other: 'Other',
    otherHint: 'Previews, log, modules',
    canvasCount: '{n} canvas|{n} canvases',
    fileCount: '{n} file|{n} files',
    browser: 'The whole browser (all profiles of this device): {used} of {quota} used',
    profileUsage: 'Used by the profile "{name}"',
    largest: 'Largest canvases of the profile',
    ofFiles: 'in files',
    backup: 'Backup',
    backupHint: "This profile's canvases live only in this browser — export saves all of them to a file",
    backupHintFolder: "This profile's canvases are files in the folder: they can be copied as they are; export saves all of them into one file",
    unused: 'Unused attachments',
    unusedHint: '{n} files, {size} — pictures no canvas of this profile uses any more',
    unusedRemoved: 'Unused attachments removed: {n}',
    remove: 'Remove',
    logs: 'Log',
    logsHint: "{size} — this profile's platform activity records, without canvas content",
    previews: 'Canvas previews',
    previewsHint: "{size} — previews of this profile's canvases, redrawn when \"My canvases\" opens",
    clear: 'Clear',
    clearAll: "Clear the profile's canvases",
    clearAllHint: 'Delete all canvases, attachments and previews of this profile. Its settings and modules stay, other profiles are not touched',
    clearAllOk: 'Delete everything',
    clearAllConfirm: 'All canvases ({n}) will be deleted with their attachments and cannot be restored. If you need a copy, export first.',
    cleared: 'Storage cleared',
  },
  about: {
    tagline: 'An infinite canvas with modules. All data stays in this browser',
    version: 'Version',
    build: 'Build',
    buildLocal: 'local',
    offline: 'Offline',
    offlineYes: 'works (the app is saved on this device)',
    offlineInstalled: 'works, installed as an app',
    offlineNo: 'no (open the app over https once with a network)',
    storagePlace: 'Canvases',
    date: 'Build date',
    type: 'Build type',
    type_development: 'Development (dev server)',
    type_local: 'Local build',
    type_preview: 'Preview',
    type_beta: 'Beta',
    type_release: 'Release',
    commit: 'Commit',
    renderer: 'Rendering',
    api: 'Module API',
    format: 'Data format',
    dskhema: 'DSkhema scheme language',
    copy: 'Copy details',
    copyHint: 'Useful when reporting a problem',
    copied: 'Details copied',
    site: 'Site',
    siteHint: 'The page about the platform',
    siteOpen: 'Open',
  },
  boolean: {
    title: 'Combine',
    hint: 'Union, subtract, intersect shapes',
    skipped: '{n} of {total} take part',
    union: 'Union',
    unionHint: 'Merge shapes into one',
    unionKey: 'Ctrl+Alt+U',
    subtract: 'Subtract',
    subtractHint: 'Cut the shapes in front out of the one at the back',
    subtractKey: 'Ctrl+Alt+S',
    intersect: 'Intersect',
    intersectHint: 'Keep only the common part',
    intersectKey: 'Ctrl+Alt+I',
    exclude: 'Exclude',
    excludeHint: 'Remove the common part, keep the rest',
    excludeKey: 'Ctrl+Alt+E',
    divide: 'Divide',
    divideHint: 'Cut into separate pieces along every edge',
    note: 'The result is an outline that looks like the back shape; one undo step. Curves become polygons.',
    empty: 'The shapes do not overlap — the result is empty',
    failed: 'These shapes could not be combined',
  },
  palette: {
    bookmark: 'Bookmark: {name}',
    title: 'Command palette',
    placeholder: 'A command or text on the canvas…',
    empty: 'Nothing found',
    group: {
      recent: 'Recent',
      edit: 'Edit',
      tools: 'Tools',
      view: 'View',
      canvas: 'Canvas',
      modules: 'Modules',
      objects: 'On the canvas',
    },
    copyStyle: 'Copy style',
    pasteStyle: 'Paste style',
    styleCopied: 'Style copied — Ctrl+Alt+V to paste',
    brush: 'Style brush',
    brushTitle: 'Style brush: click an object to give it this style',
    brushHint: 'Click an object to apply the style',
    defaultStyle: 'Default style',
    defaultStyleHint: 'New objects of this type will look the same',
    resetDefaultStyle: 'Usual style for new ones',
    keepRatio: 'Keep proportions',
    freeRatio: 'Do not keep proportions',
    showMinimap: 'Show minimap',
    hideMinimap: 'Hide minimap',
    showTree: 'Show object tree',
    hideTree: 'Hide object tree',
    guidesOn: 'Turn guides on',
    guidesOff: 'Turn guides off',
    darkTheme: 'Dark theme',
    lightTheme: 'Light theme',
    template: 'Template: {name}',
    panel: 'Panel: {name}',
  },
  bookmarks: {
    title: 'Bookmarks',
    add: 'Save the current view',
    defaultName: 'View {n}',
    empty: 'No bookmarks yet: save a place and zoom to come back to it in one click.',
    hint: 'Bookmarks are kept with the canvas. An object can link to a bookmark.',
    go: 'Go',
    rename: 'Rename',
    update: 'Remember the current view',
    up: 'Up',
    tooMany: 'No more than {n} bookmarks on a canvas',
  },
  links: {
    title: 'Link',
    add: 'Add a link',
    edit: 'Change the link',
    follow: 'Follow the link',
    remove: 'Remove',
    pick: 'Pick an object on the canvas',
    pickHint: 'Click the object the link should lead to',
    set: 'Link saved',
    object: 'object',
    missing: 'not found',
    noBookmarks: 'This canvas has no bookmarks — save a view in the bookmarks menu (the button next to the zoom).',
    noCanvases: 'There are no other canvases yet.',
    badUrl: 'An http(s) address or an e-mail is needed',
    brokenBookmark: 'The bookmark this link leads to is gone',
    brokenObject: 'The object this link leads to is gone',
    brokenCanvas: 'The canvas this link leads to is gone',
    kind: { bookmark: 'Bookmark', object: 'Object', canvas: 'Canvas', url: 'Address' },
  },
  components: {
    make: 'Create component',
    namePrompt: 'Component name',
    defaultName: 'Component {n}',
    insert: 'Insert an instance',
    insertNamed: 'Component: {name}',
    goMaster: 'Go to the master',
    reset: 'Reset instance changes',
    detach: 'Detach from the component',
    master: 'Component master',
    instance: 'Component instance',
  },
  notes: {
    title: 'Notes',
    open: 'Comments',
    reactions: 'Reactions',
    empty: 'No comments yet',
    placeholder: 'Comment… (Ctrl+Enter)',
    add: 'Add',
    done: 'Done',
  },
  legal: {
    title: 'Before you start',
    text: 'Please read the privacy policy and the terms of use. By clicking "I accept" you agree to them.',
    updatedTitle: 'The documents were updated',
    updatedText: 'We changed the privacy policy or the terms of use. Please read the new edition.',
    note: 'Your canvases stay in this browser — they are not sent to the server.',
    accept: 'I accept',
    docs: 'Documents',
    docsHint: 'Privacy policy and terms of use',
  },
  notice: {
    more: 'Learn more',
    close: 'Close',
  },
  catalog: {
    tab: 'Catalog',
    installedTab: 'Installed',
    fromFile: 'Install from file',
    devLink: 'Your own unsigned module? Turn on developer mode.',
    official: 'Official',
    install: 'Install',
    update: 'Update',
    updateTo: 'Update to {v}',
    installed: 'Installed',
    loading: 'Loading the catalog…',
    offline: 'The catalog is unavailable right now. Installed modules work as usual.',
    stale: 'The catalog server does not answer — the last saved list is shown.',
    empty: 'No modules in the catalog yet.',
    installTitle: 'Install module',
    asks: 'The module asks for access:',
    replaces: 'Version {v} is installed now — it will be replaced, data stays.',
    needsDev: 'This file is not signed by the catalog. Unsigned modules install in developer mode only.',
    trustNote: 'Catalog modules are checked and signed. A module file downloaded from the showcase installs too.',
    err: {
      offline: 'Could not reach the catalog. Check the connection and try again.',
      'not-found': 'There is no such module or version in the catalog.',
      damaged: 'The module file is damaged or changed. Installation cancelled.',
      untrusted: 'The module is signed with a key this version of the app does not trust. Installation cancelled.',
      revoked: 'The signature of this module was revoked. Installation cancelled.',
      mismatch: 'The module file does not match its signature. Installation cancelled.',
    },
  },
};

export default en;
